// ORIENTATION LAYER (Beta 1) — pure, deterministic mapping from EXISTING TWS state to student-facing
// orientation. No LLM, no pedagogy, no progression logic: it observes what TWS is already doing and
// explains it in ordinary language. Everything here is a template over existing signals.

export const JOURNEY = [
  { key: "develop", label: "Develop Ideas" },
  { key: "organize", label: "Organize" },
  { key: "reader", label: "Write for a reader" },
  { key: "refine", label: "Strengthen Sentences" },
  { key: "finish", label: "Proofread" },
];
export const stageIndex = (key) => JOURNEY.findIndex((s) => s.key === key);

// OT workspace operations that mean "organizing" (existing awaiting_operation.op / dev_op.operation values) — used
// for the NOW detail only, never to derive the stage.
const ORGANIZE_AWAIT_OPS = ["subordinate", "place", "cross_relate", "identify_superordinate", "form_whole",
  "differentiate_parts", "reconsider_thesis", "integrate"];
const ORGANIZE_DEV_OPS = ["integrate", "differentiate", "relate", "represent_whole", "name_whole", "reconsider"];
export const isOrganizingOp = (awaitingOp, devOp) => ORGANIZE_AWAIT_OPS.includes(awaitingOp || "") || ORGANIZE_DEV_OPS.includes(devOp || "");

// STAGE SIGNALS ARE THE STUDENT'S OWN ESTABLISHED STRUCTURE — never TWS's provisional differentiation / placement,
// never the operation TWS happens to be asking for or working on (those arrive with a coach reply and must not flip
// the stage — and pop an orientation — before the student has organized anything themselves).
const studentEstablished = (i) => !!i && i.provenance !== "tws_provisional" && i.node_kind !== "group"
  && i.placement_provenance !== "tws_provisional" && i.placement_provenance !== "tws_inferred";
const otHasStructure = (ot = {}) => {
  const items = ot.items || [];
  if (items.some((i) => studentEstablished(i) && (i.parent_id || i.under_thesis || (i.region || "").trim()))) return true;
  if ((ot.relationships || []).some((r) => r.provenance !== "tws_inferred" && r.provenance !== "tws_provisional")) return true;
  if (ot.hasThesis) return true;
  return false;
};

// ---------------------------------------------------------------------------------------------------
// STAGE — which of the five journey stages the student is in, from existing screen/state signals.
// ---------------------------------------------------------------------------------------------------
export function deriveStage(signals = {}) {
  const { screen, ot = {} } = signals;
  switch (screen) {
    case "ot":
      if (ot.regionsActive) return "reader";
      // ESSAY-AS-WORKSPACE (consolidated loop): marking a main idea inside the essay is not "organizing"; the organize stage
      // begins when the controller licenses visible structure (or the student places something themselves).
      if (ot.consolidated) return ot.structureLicensed ? "organize" : "develop";
      if (otHasStructure(ot)) return "organize";
      return "develop";
    case "fo": case "fsc": case "essay": case "composition":
      return "reader";
    case "sentence_craft":
      return "refine";
    case "proofreading": case "final":
      return "finish";
    default:
      return "develop";
  }
}

// ---------------------------------------------------------------------------------------------------
// NOW — one short statement of the student's current activity + a "Right now:" detail + a bounded horizon.
// ---------------------------------------------------------------------------------------------------
const OT_AWAIT_DETAIL = {
  subordinate: "deciding where this idea belongs",
  place: "deciding where this idea belongs",
  cross_relate: "deciding how two of your ideas relate",
  identify_superordinate: "finding the larger idea that holds these together",
  form_whole: "finding the larger idea that holds these together",
  name_whole: "naming the larger idea your ideas add up to",
  differentiate_parts: "telling apart the parts of one idea",
  reconsider_thesis: "reconsidering your main point",
  integrate: "bringing these ideas together",
  develop_support: "developing support for one of your ideas",
};
const OT_DEV_DETAIL = {
  externalize: "getting your ideas out where you can see them",
  integrate: "bringing related ideas together",
  differentiate: "telling apart ideas that are doing different work",
  relate: "deciding how your ideas relate",
  represent_whole: "naming the larger idea your ideas add up to",
  name_whole: "naming the larger idea your ideas add up to",
  reconsider: "reconsidering your main point",
  continue: "",
};
const COMP_ELEMENT_DETAIL = {
  THESIS: "making your main point clear to your reader",
  ELABORATION: "explaining one of your ideas more fully",
  "EVIDENCE / EXAMPLE": "showing what your example proves",
  OPENING: "opening this part so your reader knows what it's about",
  CONCLUSION: "bringing this part to a close",
};

export function deriveNow(signals = {}) {
  const { screen, ot = {}, comp = {} } = signals;
  const stage = deriveStage(signals);
  const unit = comp.multi ? "this part" : "your paragraph";
  let now = "", detail = "", horizon = "";
  if (screen === "ot" && stage === "develop") {
    now = "Developing what you want to say.";
    detail = OT_AWAIT_DETAIL[ot.awaitingOp] || OT_DEV_DETAIL[ot.devOp] || "";
    horizon = "One idea at a time. When you have enough to work with, you'll look at how they fit together.";
  } else if (screen === "ot" && stage === "organize") {
    now = "Figuring out how your ideas fit together.";
    detail = OT_AWAIT_DETAIL[ot.awaitingOp] || OT_DEV_DETAIL[ot.devOp] || "";
    if (ot.devOp === "externalize" && !ot.awaitingOp) detail = "adding what's still missing from your ideas";
    horizon = "One decision at a time. When the organization is clear enough, you'll start shaping it for a reader.";
  } else if (screen === "ot" || screen === "fo") {
    now = "Organizing your ideas into an essay for your reader.";
    detail = screen === "ot" ? "deciding which parts of your thinking open, carry, and close the essay"
      : "deciding what each part of the essay does for your reader";
    horizon = "When the parts are in an order a reader can follow, you'll begin writing them.";
  } else if (screen === "fsc") {
    now = "Deciding how to present this part to your reader.";
    detail = "drafting this part from the thinking behind it";
    horizon = "One part at a time. When this part is drafted, you'll move to the next.";
  } else if (screen === "essay") {
    now = "Shaping your draft as an essay for your reader.";
    detail = "looking at the whole before working on its parts";
    horizon = "When the whole hangs together, you'll work on one part at a time.";
  } else if (screen === "composition") {
    now = comp.multi ? "Developing this part so your reader can understand it." : "Developing your paragraph so your reader can understand it.";
    if (comp.precedenceActive) detail = "checking that your main point answers the question";
    else if (comp.closing) detail = `checking that ${unit} does its job for your reader`;
    else detail = COMP_ELEMENT_DETAIL[comp.currentElement] || "";
    horizon = comp.multi
      ? "When this part is developed enough for a reader, we'll move to the next part."
      : "When your paragraph is developed enough for a reader, you'll move on to strengthening your sentences.";
  } else if (screen === "sentence_craft") {
    now = "Making this sentence clearer.";
    detail = "revising the highlighted sentence";
    horizon = "One sentence at a time. When the sentences that need work are clear, you'll do a final check.";
  } else if (screen === "proofreading") {
    now = "Giving your writing a final check.";
    detail = "fixing the highlighted problem, or marking it as intentional";
    horizon = "When the remaining problems are handled, you're finished.";
  } else if (screen === "final") {
    now = "Reviewing your finished writing.";
    detail = "";
    horizon = "You're done with this piece.";
  }
  return { stage, now, detail: detail ? `Right now: ${detail}.` : "", action: detail, horizon };
}

// ---------------------------------------------------------------------------------------------------
// EXPANDED NOW / I'M LOST — what / how / why / next / how long, as a teacher would say it.
// ---------------------------------------------------------------------------------------------------
export function deriveExpanded(signals = {}) {
  const n = deriveNow(signals);
  const { screen, comp = {} } = signals;
  const unit = comp.multi ? "this part" : "your paragraph";
  const T = {
    develop: {
      where: "You’re developing ideas right now.",
      how: "Get your ideas out where you can see them, in your own words. They don't have to be finished thoughts.",
      why: "You can't organize thinking you haven't put into words yet. This is the raw material for everything that follows.",
      next: "Next, you'll look at how these ideas fit together.",
      howLong: "We'll stay here until you have enough ideas to start working with.",
    },
    organize: {
      where: "You’re organizing the essay right now.",
      how: "You don't have to manage the whole map. Just make the one decision in front of you — whether an idea belongs inside a larger idea, or should stay separate, or how two ideas relate.",
      why: "The organization you discover here will guide the essay for your reader.",
      next: "Next, you'll start using that organization to shape the essay for a reader.",
      howLong: "We'll stay with this only until the organization is clear enough to help you move forward.",
    },
    reader: {
      where: screen === "composition"
        ? (comp.multi ? "You're developing one part of your essay for a reader." : "You're developing your paragraph for a reader.")
        : "You're turning your thinking into an essay for a reader.",
      how: screen === "composition"
        ? `Work on ${unit} above and send your revision. "How this part is put together" is another way to see the same writing: it shows how the ideas in this part fit together, and the marked part is what you're working on right now. As your writing changes, that view changes too. You don't have to fix everything at once — The Writing Studio points to one thing at a time.`
        : "Ask one question at a time: how should I present this so someone else can follow it? You don't have to hold the whole essay in your head.",
      why: "A reader can't see your thinking. They only see what's on the page, in the order you put it.",
      next: screen === "composition"
        ? (comp.multi ? "After this, you'll work on the next part. When the parts are done, you'll strengthen your sentences." : "After this, you'll strengthen your sentences.")
        : "Next, you'll write the parts one at a time, developing each for your reader.",
      howLong: screen === "composition"
        ? `When ${unit} is developed enough for a reader, we'll move on.`
        : "When the parts are in an order a reader can follow, you'll begin writing them.",
    },
    refine: {
      where: "You’re strengthening sentences right now.",
      how: "Revise the highlighted sentence and send it. You only need to work on one sentence at a time.",
      why: "Your ideas and organization are in place. Now the sentences need to carry them clearly.",
      next: "Next, you'll proofread the writing.",
      howLong: "We'll stay here until the sentences that need work are clear.",
    },
    finish: {
      where: screen === "final" ? "You’re reviewing your finished writing." : "You’re proofreading right now.",
      how: "Fix the highlighted problem, or mark it as intentional if you meant it that way.",
      why: "Small errors can distract a reader from what you've worked hard to say.",
      next: screen === "final" ? "You're finished with this piece." : "After this, you're finished.",
      howLong: screen === "final" ? "You're done." : "When the remaining problems are handled, you're finished.",
    },
  }[n.stage];
  return { ...n, ...T };
}

// ---------------------------------------------------------------------------------------------------
// ORIENTATION MOMENTS — entry + exactly four major transitions, shown as short popups that then disappear.
// Each answers, in plain language: where am I / what am I doing / how / why / how long (what determines
// moving on) / what comes next — and reassures: you don't have to manage the whole process at once.
// ---------------------------------------------------------------------------------------------------
export const REASSURANCE = "You don't have to manage the whole process at once. Focus on what you're doing now.";

// Standard five-part student-facing structure of every entry / transition popup (same order everywhere).
export const MOMENT_SECTIONS = [
  { key: "what", label: "What you're doing" },
  { key: "why", label: "Why" },
  { key: "how", label: "How" },
  { key: "howLong", label: "How long" },
  { key: "next", label: "What's next" },
];

export const TRANSITIONS = {
  "develop>organize": {
    title: "You're organizing your ideas",
    what: "Your ideas are taking shape. Now you're figuring out how they fit together.",
    why: "So that your thinking can become an organized piece of writing for a reader.",
    how: "Look at which ideas belong together, which are larger or smaller parts, and how they relate. We'll work on one decision at a time. You do not have to organize everything at once.",
    howLong: "We'll stay here until the organization is clear enough to start shaping into writing.",
    next: "You'll start shaping your organized thinking into writing for your reader.",
  },
  "organize>reader": {
    title: "You're writing for a reader",
    what: "Your thinking has an organization now. You're turning it into writing that someone else can follow.",
    why: "A reader only sees what's on the page, in the order you put it there. They can't see your thinking.",
    how: "Take one part at a time and ask how to present it so a reader who can't see your thinking can follow it. You do not have to hold the whole essay in your head.",
    howLong: "We'll stay here until your writing is developed enough for a reader to follow it.",
    next: "You'll look more closely at your sentences and how they say what you mean.",
  },
  "reader>refine": {
    title: "You're strengthening your sentences",
    what: "Your writing now does its job for a reader. You're looking more closely at how your sentences say it.",
    why: "Your ideas and organization are in place. Now the sentences need to carry them clearly.",
    how: "I'll highlight one sentence. Revise it and send it. We'll work on one sentence at a time. You do not have to rewrite everything.",
    howLong: "We'll stay here until the sentences that need work are clear.",
    next: "You'll give the writing a final check for problems that should be corrected before you're finished.",
  },
  "refine>finish": {
    title: "You're finishing your writing",
    what: "You're nearly there. You're giving your writing a final check.",
    why: "Small errors can distract a reader from what you've worked hard to say.",
    how: "I'll highlight one problem. Fix it, or mark it as intentional if you meant it that way. We'll handle one problem at a time. You do not have to find them all yourself.",
    howLong: "We'll stay here until the remaining problems are handled.",
    next: "You're finished with this piece.",
  },
};

export const ENTRY = {
  develop: {
    title: "You're developing your ideas",
    what: "Get your ideas out where you can see them, in your own words. They don't have to be finished thoughts.",
    why: "You can't organize thinking you haven't put into words yet. These ideas are the raw material you'll work with.",
    how: "Say what you think. I'll help you develop it. You don't have to figure out the whole paper at once.",
    howLong: "We'll stay here until you have enough developed thinking to begin seeing how the ideas fit together.",
    next: "You'll start organizing your ideas\u2014figuring out what belongs together and how the parts relate.",
  },
  reader_composition: {
    title: "You're writing for a reader",
    what: "You're starting with writing you already have. Now you're developing it so a reader can understand it.",
    why: "A reader can't see your thinking. They only see what's on the page.",
    how: "Put your writing in the space above and send it. I'll read it the way a reader would and point to one thing at a time \u2014 a point that needs to be clearer, an idea that needs more explanation, an example that needs to show what it proves. You revise and send again. You do not have to fix everything at once.",
    howLong: "We'll stay here until your writing is developed enough for a reader to follow it.",
    next: "You'll look more closely at your sentences and how they say what you mean.",
  },
};

// Orientation for the FIRST entry into the writing process (or a resume in a fresh browser): the stage the
// student is actually starting in.
export function entryFor(signals = {}) {
  const stage = deriveStage(signals);
  if (stage === "develop") return ENTRY.develop;
  if (stage === "reader" && signals.pathway === "composition") return ENTRY.reader_composition;
  return TRANSITIONS[`${JOURNEY[stageIndex(stage) - 1].key}>${stage}`];
}

// ---------------------------------------------------------------------------------------------------
// PROACTIVE DIFFICULTY (minimal beta) — deterministic, from the student's OWN bare message only. Ordinary
// struggle (a pause, an imperfect answer, several turns, normal scaffolding) never triggers. Only an
// explicit, unelaborated "I don't know" / confusion / "what do you mean?" does; anything longer or more
// specific is an attempt or a real question and passes straight through to the existing coach.
// ---------------------------------------------------------------------------------------------------
export const DIFFICULTY_MAX_WORDS = 10;
export const DIFFICULTY_COOLDOWN = 4;      // student sends before another prompt may appear
export const OP_REISSUE_LIMIT = 2;         // "I've done this" answered by the SAME requested move, twice
const FILLER = "(?:(?:so|ok|okay|um|uh|hmm|well|sorry|wait|honestly|really|yeah|no)\\s+)*";
const DIFF_PATTERNS = [
  ["asks_meaning", new RegExp(`^${FILLER}(what do you mean|what does (that|this|it) mean|what are you asking( me)?( to do)?|what do you want( me to do| from me)?|what am i supposed to (do|say|write)( here| now)?|what should i (do|say|write)( here| now)?|what do i do( now| here)?|what is this asking( me)?|i (dont|do not) (know|understand|get) what (you|youre|you are) (mean|want|asking|saying)|huh|what)$`)],
  ["dont_know", new RegExp(`^${FILLER}((i )?(really |just |honestly )?(dont|do not) know( (what|how|where) to (say|do|write|start|begin|answer))?( this| that| the answer| anymore| here)?|idk|dunno|(i have )?no idea|(im |i am )?not sure( what to (say|do|write))?)$`)],
  ["confused", new RegExp(`^${FILLER}((im |i am )?(so |really |totally |completely |very |a bit |a little |kind of |kinda )?(confused|lost)( now| here)?|(this is|thats|that is|its|it is) (so |really |very )?confusing|(i )?(dont|do not) (get|understand|follow)( it| this| that| the question| any of this| what to do)?)$`)],
];

export function detectDifficultyPhrase(message = "") {
  const norm = String(message).toLowerCase().replace(/[’']/g, "").replace(/[^a-z ]/g, " ").replace(/\s+/g, " ").trim();
  if (!norm || norm.split(" ").length > DIFFICULTY_MAX_WORDS) return null;
  for (const [kind, re] of DIFF_PATTERNS) if (re.test(norm)) return kind;
  return null;
}

// Per-session send counter + cooldown so the prompt never nags. Returns {trigger, memory} without mutating input.
export function difficultyCheck(message, memory = {}) {
  const m = { ...memory, sends: (memory.sends || 0) + 1 };
  const kind = detectDifficultyPhrase(message);
  const cooled = !m.lastPromptSend || m.sends - m.lastPromptSend > DIFFICULTY_COOLDOWN;
  if (kind && cooled) { m.lastPromptSend = m.sends; return { trigger: kind, memory: m }; }
  return { trigger: null, memory: m };
}

// The coach asked for the same workspace move again (same operation over the same cards) after the student said
// they had done it — existing awaiting_operation state, compared deterministically.
export function sameRequestedOperation(a, b) {
  if (!a || !b || !a.operation || a.operation !== b.operation) return false;
  const k = (x) => [...(x.source_ids || [])].sort().join(",") + "|" + [...(x.target_ids || [])].sort().join(",");
  return k(a) === k(b);
}

export const DIFFICULTY_PROMPT = ["Looks like this part may not be clear yet.", "What's getting in the way?"];
// route: coach = hand the stated difficulty to the EXISTING coach (its own scaffolding responds);
//        orient = reconnect to purpose / the larger writing process (this layer), then coach if still stuck;
//        student = the student explains in their own words (focus the existing input).
export const DIFFICULTY_OPTIONS = [
  { key: "what", label: "I don't understand what you're asking me to do.", route: "coach", message: "I don't understand what you're asking me to do." },
  { key: "how", label: "I understand it, but I don't know how to do it.", route: "coach", message: "I understand what you're asking, but I don't know how to do it." },
  { key: "why", label: "I don't understand how this connects to my paper.", route: "orient", message: "I don't understand how this connects to my paper." },
  { key: "other", label: "Something else — I'll tell you.", route: "student" },
];
export const NEED_HELP_MESSAGE = "I need help doing this.";

export const INTRO = {
  title: "You'll develop your ideas, organize them, write them for a reader, strengthen the sentences, and proofread.",
  body: ["Writing isn't perfectly linear. You may move back and forth as your ideas develop.",
    "You don't need to manage the whole process. TWS will help you focus on what matters now."],
};

export function transitionFor(prevStage, nextStage) {
  const a = stageIndex(prevStage), b = stageIndex(nextStage);
  if (a < 0 || b < 0 || b <= a) return null;                      // first observation or recursion back: nothing
  return `${JOURNEY[b - 1].key}>${nextStage}`;                   // the boundary INTO the new stage
}

// ---------------------------------------------------------------------------------------------------
// REORIENTATION EVENTS — small contextual messages, only when a student might think "why this now?".
// `memory` is the persisted per-session record; returns {events:[{key,text}], memory} without mutating input.
// ---------------------------------------------------------------------------------------------------
export const COMPLEXITY_ITEMS = 8;

export function detectEvents(signals = {}, memory = {}) {
  const m = { ...memory, seen: { ...(memory.seen || {}) } };
  const events = [];
  const n = deriveNow(signals);
  const { screen, ot = {}, comp = {} } = signals;
  const once = (key, text) => { if (!m.seen[key]) { m.seen[key] = true; events.push({ key, text }); } };

  // A. complexity increases (OT map grows past the threshold) — once
  const items = (ot.items || []).length;
  if (screen === "ot" && items >= COMPLEXITY_ITEMS && (m.itemsPeak || 0) < COMPLEXITY_ITEMS) {
    once("complexity", `There's more here now because your thinking has become more developed. You don't need to manage all of it at once.${n.action ? ` For now, just focus on this: ${n.action}.` : ""}`);
  }
  m.itemsPeak = Math.max(m.itemsPeak || 0, items);

  if (screen === "composition") {
    // B / C. move upward to the larger structure, then return downward — on edges only
    const prev = m.precedence;
    if (comp.precedenceActive && prev === false) {
      m.seen.upwardCount = (m.seen.upwardCount || 0) + 1;
      events.push({ key: `upward-${m.seen.upwardCount}`, text: "We're looking at the larger structure for a moment. Something you discovered here may affect how the whole essay works." });
    } else if (!comp.precedenceActive && prev === true) {
      m.seen.downwardCount = (m.seen.downwardCount || 0) + 1;
      events.push({ key: `downward-${m.seen.downwardCount}`, text: "That gives us what we need. Now let's return to this part and keep working on how to present it to your reader." });
    }
    m.precedence = !!comp.precedenceActive;
    // D. new essay part / section (after the first one)
    const secKey = comp.section ? `${comp.section.sectionId || comp.section.title || ""}#${comp.section.paragraphIndex ?? 0}` : "";
    if (secKey && m.section && secKey !== m.section) {
      const name = comp.section.title ? `“${comp.section.title}”` : "the next part";
      once(`section:${secKey}`, `Now you're working on ${name}. For now, focus on how to present this part so it helps your reader follow the larger essay.`);
    }
    if (secKey) m.section = secKey;
    // E. an assignment requirement resurfaces (a demand was set aside and preserved at the essay level)
    const open = comp.openDemands || 0;
    if (open > (m.openDemands || 0)) {
      once(`demand:${open}`, "Something the assignment asks for still needs a place. It didn't necessarily belong where you were working before. Now let's decide where — or whether — it belongs in the essay.");
    }
    m.openDemands = Math.max(m.openDemands || 0, open);
    // F. immediate focus when the screen becomes thick (map + coaching + writing all present) — once
    if (comp.hasMap && n.action) once("focus", `Just focus on this: ${n.action}. You don't need to manage the rest right now.`);
  }
  return { events, memory: m };
}
